import { useTeams } from "@/hooks/use-teams";

const TIPO_CONTRATO_OPTIONS = [
  { value: "", label: "Seleccionar tipo de contrato..." },
  { value: "SERVICIOS", label: "Servicios" },
  { value: "SUMINISTROS", label: "Suministros" },
  { value: "OBRAS", label: "Obras" },
  { value: "CONCESION_SERVICIOS", label: "Concesión de Servicios" },
  { value: "CONCESION_OBRAS", label: "Concesión de Obras" },
  { value: "MIXTO", label: "Mixto" },
];

interface FormStep1Props {
  data: {
    titulo: string;
    referencia: string;
    descripcion: string;
    organismo: string;
    tipo_contrato: string;
    team_id: string;
  };
  onChange: (data: Partial<FormStep1Props["data"]>) => void;
  errors: Record<string, string>;
  /** Si true, el selector de equipo se oculta (modo edición). */
  hideTeamSelector?: boolean;
}

const inputClass = (field: string, errors: Record<string, string>) =>
  `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
    errors[field] ? "border-red-500" : "border-gray-300"
  }`;

export function LicitacionFormStep1({
  data,
  onChange,
  errors,
  hideTeamSelector,
}: FormStep1Props) {
  // En modo edición no mostramos el selector: la licitación se queda en su
  // equipo de origen (reasignar es operación admin, fuera de este wizard).
  const { data: teams, isLoading: loadingTeams } = useTeams({
    onlyMine: true,
  });

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="titulo" className="block text-sm font-medium text-gray-700 mb-1">
          Título <span className="text-red-500">*</span>
        </label>
        <input
          id="titulo"
          type="text"
          value={data.titulo}
          onChange={(e) => onChange({ titulo: e.target.value })}
          className={inputClass("titulo", errors)}
          placeholder="Título de la licitación"
        />
        {errors.titulo && <p className="text-sm text-red-500 mt-1">{errors.titulo}</p>}
      </div>

      <div>
        <label htmlFor="referencia" className="block text-sm font-medium text-gray-700 mb-1">
          Referencia
        </label>
        <input
          id="referencia"
          type="text"
          value={data.referencia}
          onChange={(e) => onChange({ referencia: e.target.value })}
          className={inputClass("referencia", errors)}
          placeholder="Número de referencia (opcional)"
        />
      </div>

      <div>
        <label htmlFor="descripcion" className="block text-sm font-medium text-gray-700 mb-1">
          Descripción
        </label>
        <textarea
          id="descripcion"
          rows={3}
          value={data.descripcion}
          onChange={(e) => onChange({ descripcion: e.target.value })}
          className={inputClass("descripcion", errors)}
          placeholder="Descripción de la licitación (opcional)"
        />
      </div>

      <div>
        <label htmlFor="organismo" className="block text-sm font-medium text-gray-700 mb-1">
          Organismo <span className="text-red-500">*</span>
        </label>
        <input
          id="organismo"
          type="text"
          value={data.organismo}
          onChange={(e) => onChange({ organismo: e.target.value })}
          className={inputClass("organismo", errors)}
          placeholder="Organismo contratante"
        />
        {errors.organismo && <p className="text-sm text-red-500 mt-1">{errors.organismo}</p>}
      </div>

      <div>
        <label htmlFor="tipo_contrato" className="block text-sm font-medium text-gray-700 mb-1">
          Tipo de contrato <span className="text-red-500">*</span>
        </label>
        <select
          id="tipo_contrato"
          value={data.tipo_contrato}
          onChange={(e) => onChange({ tipo_contrato: e.target.value })}
          className={inputClass("tipo_contrato", errors)}
        >
          {TIPO_CONTRATO_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {errors.tipo_contrato && <p className="text-sm text-red-500 mt-1">{errors.tipo_contrato}</p>}
      </div>

      {!hideTeamSelector && (
        <div>
          <label htmlFor="team_id" className="block text-sm font-medium text-gray-700 mb-1">
            Equipo <span className="text-red-500">*</span>
          </label>
          <select
            id="team_id"
            value={data.team_id}
            onChange={(e) => onChange({ team_id: e.target.value })}
            className={inputClass("team_id", errors)}
            disabled={loadingTeams}
          >
            <option value="">
              {loadingTeams
                ? "Cargando equipos…"
                : teams && teams.length > 0
                  ? "Seleccionar equipo…"
                  : "No perteneces a ningún equipo"}
            </option>
            {teams?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          {errors.team_id && (
            <p className="text-sm text-red-500 mt-1">{errors.team_id}</p>
          )}
          <p className="text-[11px] text-gray-400 mt-1">
            Solo los miembros del equipo y los administradores podrán ver
            esta licitación.
          </p>
        </div>
      )}
    </div>
  );
}
